package com.trevorwiebe.timesheet.admin.employeeHours.domain

import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.TimeSheet
import com.trevorwiebe.timesheet.payPeriod.presentation.uiUtils.TimeSheetStatus

/**
 * One employee's row in the Employee Hour Reports list: their hours + submission status for the
 * current pay period and the previous one. Mirrors the legacy admin `employee-hours` page.
 *
 * [previousTimeSheet] is the underlying timesheet document for the previous pay period (null when
 * the employee has none). It drives the CSV export gate (`confirmedByUser`) and the
 * mark-submitted write (`id`).
 */
data class EmployeeHoursSummary(
    val employee: DatabaseUserModel,
    val current: PeriodSummary,
    val previous: PeriodSummary,
    val previousTimeSheet: TimeSheet? = null,
)

/**
 * Hours for a single pay period, broken down by rate (e.g. "Regular Hours" to 40.0), plus the
 * timesheet submission status. [status] is empty when no timesheet document exists for the period.
 */
data class PeriodSummary(
    val hoursByRate: List<Pair<String, Double>>,
    val status: List<TimeSheetStatus>,
)

/**
 * Whether the admin may export the previous pay period to payroll: there must be at least one
 * previous-period timesheet, and every employee who has one must have confirmed it
 * (`confirmedByUser`). Employees without a previous-period timesheet are ignored (legacy parity).
 */
fun List<EmployeeHoursSummary>.canExportPreviousPeriod(): Boolean {
    val previous = mapNotNull { it.previousTimeSheet }
    return previous.isNotEmpty() && previous.all { it.confirmedByUser }
}
